import {
  IconAbortCircleFilled,
  IconCheckCircle,
  IconCircle,
  IconCrossCircleFilled,
  IconDoubleCircle,
  IconSkipCircle,
} from '@bitrise/bitkit-v2';

import { ERROR_CHUNK_MAGIC_CODE } from '@/components/LogViewer/LogErrorMarker';
import { StatusDecorator } from '@/types/StatusDecorator';
import { integrationsStepPath } from '@/utils/monolithExternalRouteService';

import { StepSummaryError, StepSummaryStatus } from '../../../types/BuildLogSummary';
import { StepLogChunk, StepLogChunkLogLevel } from '../../../types/StepLogResponse';

export const LogLevelColor: Record<
  StepLogChunkLogLevel,
  | 'color/turquoise/bold'
  | 'color/white/100'
  | 'color/green/bold'
  | 'color/red/bold'
  | 'color/yellow/muted'
  | 'color/blue/bold'
> = {
  [StepLogChunkLogLevel.Debug]: 'color/turquoise/bold',
  [StepLogChunkLogLevel.Normal]: 'color/white/100',
  [StepLogChunkLogLevel.Done]: 'color/green/bold',
  [StepLogChunkLogLevel.Error]: 'color/red/bold',
  [StepLogChunkLogLevel.Warn]: 'color/yellow/muted',
  [StepLogChunkLogLevel.Info]: 'color/blue/bold',
};

const LogLevelAnsiSequence: Record<StepLogChunkLogLevel, string> = {
  [StepLogChunkLogLevel.Debug]: '\u001b[36m', // cyan
  [StepLogChunkLogLevel.Normal]: '', // white
  [StepLogChunkLogLevel.Info]: '\u001b[34m', // blue
  [StepLogChunkLogLevel.Error]: `\u001b[1;31m\u001b[${ERROR_CHUNK_MAGIC_CODE}m`, // bold red
  [StepLogChunkLogLevel.Done]: '\u001b[1;32m', // bold green
  [StepLogChunkLogLevel.Warn]: '\u001b[1;33m', // bold yellow
};

export function wrapWithAnsiColor(chunk: StepLogChunk) {
  const color = LogLevelAnsiSequence[chunk.level] || LogLevelAnsiSequence[StepLogChunkLogLevel.Normal];
  const reset = '\u001b[0m';
  return color ? `${color}${chunk.message}${reset}` : chunk.message;
}

export function isStepOpenByDefault(status: StepSummaryStatus): boolean {
  return [
    StepSummaryStatus.PreparationFailed,
    StepSummaryStatus.FailedSkippable,
    StepSummaryStatus.Failed,
    StepSummaryStatus.AbortedWithCustomTimeout,
    StepSummaryStatus.AbortedWithNoOutput,
  ].includes(status);
}

export function getStepStatusDecorator(status?: StepSummaryStatus): StatusDecorator {
  switch (status) {
    case StepSummaryStatus.Started:
      return {
        icon: IconDoubleCircle,
        text: 'Started',
        textColor: 'icon/secondary',
        backgroundColor: 'background/secondary',
      };
    case StepSummaryStatus.Skipped:
    case StepSummaryStatus.SkippedWithRunIf:
      return {
        icon: IconSkipCircle,
        text: 'Skipped',
        textColor: 'icon/secondary',
        backgroundColor: 'background/secondary',
      };
    case StepSummaryStatus.Aborted:
      return {
        icon: IconAbortCircleFilled,
        text: 'Aborted',
        textColor: 'status/warning/text',
        backgroundColor: 'status/warning/bg',
      };
    case StepSummaryStatus.PreparationFailed:
    case StepSummaryStatus.FailedSkippable:
    case StepSummaryStatus.Failed:
    case StepSummaryStatus.AbortedWithCustomTimeout:
    case StepSummaryStatus.AbortedWithNoOutput:
      return {
        icon: IconCrossCircleFilled,
        text: 'Failed',
        textColor: 'status/critical/text',
        backgroundColor: 'status/critical/bg',
      };
    case StepSummaryStatus.Success:
      return {
        icon: IconCheckCircle,
        text: 'Success',
        textColor: 'status/success/text',
        backgroundColor: 'status/success/bg',
      };
    case StepSummaryStatus.NotStarted:
    default:
      return {
        icon: IconCircle,
        text: 'Not started',
        textColor: 'icon/secondary',
        backgroundColor: 'background/secondary',
      };
  }
}

export const formatErrorMessage = ({ message, code }: StepSummaryError) => {
  const match = message.match(/^(?:[^\r\n]*(?:\r\n?|\n)){0,3}[^\r\n]*/g);
  return `${match?.length ? match[0] : message}${code ? ` (exit code: ${code})` : ''}`;
};

export function createDocumentationUrl(stepId: string): string | undefined {
  if (stepId.includes('::') || stepId.includes('@')) {
    return;
  }

  return integrationsStepPath(stepId);
}
